/**
 * "New Messages" divider for chat (Mission 20 unified – web, iOS, Android).
 * Renders a teal line with "New Messages" label between two lines.
 * Shown when the user returns to a chat and there are unread messages below
 * their last scroll position. Disappears when the user scrolls past it or
 * reaches the bottom (go-away logic handled by the parent Chat component).
 */

import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { Spacing, CoachCedarColors } from '@/utils/styleConverter';

// New-messages divider accent — cedarPrime (mint fails contrast as a thin line/label on white)
const DIVIDER_LINE = CoachCedarColors.cedarPrime;

export function NewMessagesDivider() {
  const lineEl = Platform.OS === 'web' ? (
    <div style={webStyles.line as React.CSSProperties} />
  ) : (
    <View style={styles.line} />
  );

  if (Platform.OS === 'web') {
    return (
      <div className="new-messages-divider" style={webStyles.wrapper as React.CSSProperties}>
        {lineEl}
        <span style={webStyles.label as React.CSSProperties}>New Messages</span>
        {lineEl}
      </div>
    );
  }

  return (
    <View style={styles.wrapper}>
      {lineEl}
      <Text style={styles.label}>New Messages</Text>
      {lineEl}
    </View>
  );
}

// Native styles (iOS / Android)
const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingVertical: Spacing.md,
    paddingHorizontal: 0,
    gap: 8,
  },
  line: {
    flex: 1,
    height: 1.5,
    backgroundColor: DIVIDER_LINE,
    opacity: 0.85,
  },
  label: {
    color: DIVIDER_LINE,
    fontSize: 12,
    fontWeight: '600',
    paddingHorizontal: 12,
    flexShrink: 0,
    letterSpacing: 0.3,
  },
});

// Web inline styles (CSSProperties) – mirrors the native values
const webStyles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingTop: Spacing.md,
    paddingBottom: Spacing.md,
    paddingLeft: 0,
    paddingRight: 0,
    gap: 8,
  },
  line: {
    flex: 1,
    height: 1.5,
    backgroundColor: DIVIDER_LINE,
    opacity: 0.85,
  },
  label: {
    color: DIVIDER_LINE,
    fontSize: 12,
    fontWeight: '600' as const,
    paddingLeft: 12,
    paddingRight: 12,
    flexShrink: 0,
    letterSpacing: 0.3,
    whiteSpace: 'nowrap' as const,
  },
};
